import { useEffect } from "react";
import { RouterProvider } from "react-router/dom";
import router from "@/router";
import { AuthProvider, useAuth } from "@/features/auth";
import { CartProvider } from "@/features/cart";
import { useGlobalUserChannel } from "@/features/chat/hooks/useGlobalUserChannel";
import { DealsCartProvider } from "@/features/deals/context/DealsCartContext";
import { NotificationsProvider } from "@/features/notifications";
import { BrandThemeProvider } from "@/features/theme";
import { VoucherDrawerProvider } from "@/features/vouchers";
import { Sentry } from "@/shared/clients/sentry.client";
import { ErrorPageView } from "@/shared/components/ErrorPageView";
import { InactivityDialog } from "@/shared/components/InactivityDialog";
import { AppDependenciesProvider } from "@/shared/context/AppDependenciesContext";
import { OnboardingProvider } from "@/shared/context/OnboardingContext";
import { UnifiedCartProvider } from "@/shared/context/UnifiedCartContext";
import { useActivityChannel } from "@/shared/hooks/useActivityChannel";
import { useRouteViewTracking } from "@/shared/hooks/useRouteViewTracking";
import { usageTracker } from "@/shared/lib/usageTracker";

const ROOT_ERROR_TITLE = "Une erreur est survenue";
const ROOT_ERROR_DESCRIPTION =
  "Nous sommes désolés, une erreur inattendue s'est produite. Notre équipe a été notifiée et travaille à résoudre le problème.";

const RootErrorFallback = () => (
  <ErrorPageView
    title={ROOT_ERROR_TITLE}
    description={ROOT_ERROR_DESCRIPTION}
    onBackHome={() => {
      window.location.href = "/";
    }}
    showSupportText
  />
);

function App() {
  return (
    <Sentry.ErrorBoundary fallback={RootErrorFallback} showDialog={false}>
      <AppDependenciesProvider enableDevtools>
        <AuthProvider>
          <BrandThemeProvider>
            <NotificationsProvider>
              <CartProvider>
                <DealsCartProvider>
                  <UnifiedCartProvider>
                    <OnboardingProvider>
                      <VoucherDrawerProvider>
                        <RouterProvider router={router} />
                      </VoucherDrawerProvider>
                    </OnboardingProvider>
                    <ChatUnreadListener />
                    <AuthenticatedInactivityListener />
                    <EmployeeUsageTracking />
                  </UnifiedCartProvider>
                </DealsCartProvider>
              </CartProvider>
            </NotificationsProvider>
          </BrandThemeProvider>
        </AuthProvider>
      </AppDependenciesProvider>
    </Sentry.ErrorBoundary>
  );
}

const ChatUnreadListener = () => {
  useGlobalUserChannel();
  return null;
};

const AuthenticatedInactivityListener = () => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return null;
  }

  return <InactivityListener />;
};

const EmployeeUsageTracking = () => {
  const { isAuthenticated, user } = useAuth();
  const employee = user?.employee;

  if (!isAuthenticated || !employee || employee.impersonation?.active) {
    return null;
  }

  return <UsageTrackingListener />;
};

const UsageTrackingListener = () => {
  useEffect(() => {
    usageTracker.start();
    return () => usageTracker.stop();
  }, []);
  useRouteViewTracking(router, usageTracker.trackView);
  return null;
};

const InactivityListener = () => {
  const {
    showInactivityDialog,
    inactivityMessage,
    warningLevel,
    isHandlingActivity,
    countdown,
    handleCloseDialog,
  } = useActivityChannel();

  return (
    <InactivityDialog
      open={showInactivityDialog}
      message={inactivityMessage}
      warningLevel={warningLevel}
      isHandlingActivity={isHandlingActivity}
      countdown={countdown}
      onClose={handleCloseDialog}
    />
  );
};

export default App;
